import { useState } from 'react';
import { Link } from 'react-router-dom';
import { X, PaperPlaneTilt, SpinnerGap, Check, WarningCircle, Gear } from '@phosphor-icons/react';
import api from '@/lib/api';
import RichTextEditor from './RichTextEditor';

/**
 * Modal de composição/envio de email aos CLIENTES seleccionados da rede.
 * Props:
 *  - selectedClients: [{ ID_Cliente, NomeCompleto, Email }]
 *  - onClose(), onSent(report)
 */
export default function BulkEmailClientsDialog({ selectedClients, onClose, onSent }) {
  const [subject, setSubject] = useState('');
  const [bodyHtml, setBodyHtml] = useState('');
  const [sending, setSending] = useState(false);
  const [report, setReport] = useState(null);
  const [error, setError] = useState('');

  const ids = selectedClients.map(c => c.ID_Cliente);
  const missingEmail = selectedClients.filter(c => !c.Email);
  const canSend = subject.trim() && bodyHtml.replace(/<[^>]*>/g, '').trim() && ids.length > 0 && !sending;

  const handleSend = async () => {
    setSending(true); setError(''); setReport(null);
    try {
      const r = await api.post('/clientes/bulk-email', {
        ID_Clientes: ids, subject: subject.trim(), body_html: bodyHtml,
      });
      setReport(r.data); onSent?.(r.data);
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || 'Erro desconhecido');
    } finally { setSending(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4"
      data-testid="bulk-email-clients-dialog">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[92vh] flex flex-col overflow-hidden">
        <div className="px-6 py-4 border-b border-[#E5E7EB] flex items-start justify-between gap-4">
          <div>
            <h3 className="text-lg font-semibold text-[#111827]" style={{ fontFamily: "'Fraunces', sans-serif" }}>Enviar email aos clientes</h3>
            <p className="text-xs text-[#6B7280] mt-1">
              {selectedClients.length} cliente(s) seleccionado(s){missingEmail.length > 0 ? ` — ${missingEmail.length} sem email serão ignorados` : ''}
            </p>
          </div>
          <button onClick={onClose} data-testid="bulk-email-clients-close"
            className="w-8 h-8 rounded-full hover:bg-[#F3F4F6] flex items-center justify-center flex-shrink-0"><X size={18} /></button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
          {report ? (
            <BulkReport report={report} />
          ) : (
            <>
              <div>
                <label className="text-xs font-medium text-[#374151] block mb-1">Assunto</label>
                <input type="text" value={subject} onChange={e => setSubject(e.target.value)}
                  data-testid="bulk-email-clients-subject" placeholder="Ex: Informação importante"
                  className="w-full border border-[#E5E7EB] rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-[#0F1829]/20 focus:border-[#0F1829] outline-none" />
              </div>
              <div>
                <label className="text-xs font-medium text-[#374151] block mb-1">Corpo do email (HTML)</label>
                <RichTextEditor value={bodyHtml} onChange={setBodyHtml} testId="bulk-email-clients-body"
                  placeholder="Escreva a mensagem para os clientes seleccionados…" />
                <p className="text-[11px] text-[#6B7280] mt-1">
                  A sua assinatura corporativa e o aviso legal são adicionados automaticamente; o email usa o layout oficial IHTP.
                </p>
              </div>
              <details className="text-xs">
                <summary className="cursor-pointer text-[#6B7280] hover:text-[#0F1829]">Ver destinatários ({selectedClients.length})</summary>
                <div className="mt-2 max-h-32 overflow-y-auto border border-[#E5E7EB] rounded-md">
                  <ul className="text-xs divide-y divide-[#F3F4F6]">
                    {selectedClients.map(c => (
                      <li key={c.ID_Cliente} className="px-3 py-1.5 flex items-center justify-between">
                        <span className="text-[#111827]">{c.NomeCompleto || '-'}</span>
                        <span className={`font-mono ${c.Email ? 'text-[#6B7280]' : 'text-red-600'}`}>{c.Email || '(sem email)'}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </details>
              {error && (
                <div data-testid="bulk-email-clients-error" className="bg-red-50 border border-red-200 text-red-800 text-xs px-3 py-2 rounded-md flex items-start gap-2">
                  <WarningCircle size={16} className="flex-shrink-0 mt-0.5" />
                  <div>
                    <p>{error}</p>
                    {(error || '').includes('SMTP') && (
                      <Link to="/perfil/email-config" onClick={onClose} className="inline-flex items-center gap-1 mt-1.5 font-semibold underline hover:no-underline">
                        <Gear size={12} /> Ir para configuração de email
                      </Link>
                    )}
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        <div className="px-6 py-3 border-t border-[#E5E7EB] bg-[#F9FAFB] flex items-center justify-end gap-2">
          {report ? (
            <button onClick={onClose} data-testid="bulk-email-clients-done" className="px-4 py-2 text-sm font-medium bg-[#0F1829] text-white rounded-md hover:bg-[#002480]">Fechar</button>
          ) : (
            <>
              <button onClick={onClose} className="px-4 py-2 text-sm font-medium text-[#374151] hover:bg-[#F3F4F6] rounded-md">Cancelar</button>
              <button onClick={handleSend} disabled={!canSend} data-testid="bulk-email-clients-send"
                className="px-4 py-2 text-sm font-medium bg-[#0F1829] text-white rounded-md hover:bg-[#002480] disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1.5">
                {sending ? <SpinnerGap size={14} className="animate-spin" /> : <PaperPlaneTilt size={14} />}
                {sending ? 'A enviar…' : `Enviar (${ids.length})`}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function BulkReport({ report }) {
  const { sent_count = 0, failed_count = 0, skipped_count = 0, failed = [], skipped = [] } = report;
  const Stat = ({ label, value, cls }) => (
    <div className={`border rounded-md px-3 py-2 ${cls}`}>
      <p className="text-[10px] uppercase tracking-[0.15em] font-medium opacity-80">{label}</p>
      <p className="text-xl font-bold">{value}</p>
    </div>
  );
  return (
    <div data-testid="bulk-email-clients-report" className="space-y-4">
      <div className="grid grid-cols-3 gap-3">
        <Stat label="Enviados" value={sent_count} cls="bg-green-50 border-green-200 text-green-800" />
        <Stat label="Falharam" value={failed_count} cls="bg-red-50 border-red-200 text-red-800" />
        <Stat label="Ignorados" value={skipped_count} cls="bg-amber-50 border-amber-200 text-amber-800" />
      </div>
      {failed.length > 0 && (
        <ul className="text-xs divide-y divide-red-100 border border-red-200 rounded-md bg-red-50 max-h-32 overflow-y-auto">
          {failed.map((f, i) => (
            <li key={i} className="px-3 py-1.5 flex items-center justify-between gap-3">
              <span className="text-red-900">#{f.id_cliente} {f.to || ''}</span>
              <span className="font-mono text-red-700 text-[10px] truncate max-w-[300px]" title={f.error}>{f.error}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
